/**
 * @fileoverview Page-level heading that names the active date-filter range.
 * Mirrors the DatePresetSelector captions exactly (shared classifier); a
 * range matching no preset renders as explicit dates, e.g. "1 – 11 May".
 * @module shared/components/filters/DateRangeHeading
 */
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils/cn'
import { useFilter } from '@/shared/hooks/useFilter'
import { classifyDateRange, formatDateRange } from '@/shared/utils/dateUtils'

const MONTH_SHORT_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const

/**
 * Heading announcing the active date range, e.g. "This month" or "1 – 11 May".
 *
 * @example
 * ```tsx
 * <DateRangeHeading className="ml-auto" />
 * ```
 */
export function DateRangeHeading({ className }: { className?: string }): ReactElement {
  const { t, i18n } = useTranslation('common')
  const { startDate, endDate } = useFilter()
  const preset = classifyDateRange(startDate, endDate)

  const label = (() => {
    switch (preset) {
      case 'today':
        return t('timeRanges.today')
      case 'yesterday':
        return t('timeRanges.yesterday')
      case 'this-month':
        return t('timeRanges.thisMonth')
      case 'previous-month':
        return t('timeRanges.previousMonth')
      case 'this-year':
        return t('timeRanges.thisYear')
      default: {
        if (preset.startsWith('month-')) {
          const idx = Number.parseInt(preset.replace('month-', ''), 10)
          const key = MONTH_SHORT_KEYS[idx]
          if (key !== undefined) return t(`monthsShort.${key}`)
        }
        return startDate && endDate
          ? formatDateRange(startDate, endDate, i18n.language)
          : t('timeRanges.customRange')
      }
    }
  })()

  return (
    <h1
      data-testid="date-range-heading"
      className={cn('text-2xl font-semibold tracking-tight text-muted-foreground/70', className)}
    >
      {label}
    </h1>
  )
}
